import {
  AlertTriangle,
  ChevronRight,
  FileSearch,
  Library,
  LoaderCircle,
  PanelLeftOpen,
  RefreshCw,
  Search,
  Sparkles,
  X,
} from "lucide-react";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { AssistantSourceCard } from "./components/AssistantSourceCard";
import { AssistantTopBar } from "./components/AssistantTopBar";
import { BrandMark } from "./components/BrandMark";
import { KnowledgeSidebar } from "./components/KnowledgeSidebar";
import { McsChatWorkspace } from "./components/McsChatWorkspace";
import { McsSourcePreviewModal } from "./components/McsSourcePreviewModal";
import {
  getCatalog,
  getModule,
  getSearchIndex,
  getSourceBrief,
  searchHub,
  type SearchIndexItem,
} from "./lib/api";
import type {
  HubCatalog,
  HubModule,
  HubSearchResponse,
  HubSearchResult,
  HubSourceBrief,
  SearchResultType,
} from "./lib/types";

type InitialLocation = {
  query: string;
  moduleId: string;
  chapterId: string;
};

export default function App() {
  const initialLocation = useMemo(readLocation, []);
  const isEmbedded = useMemo(() => window.self !== window.top, []);
  const [catalog, setCatalog] = useState<HubCatalog | null>(null);
  const [searchIndex, setSearchIndex] = useState<SearchIndexItem[]>([]);
  const [loadError, setLoadError] = useState("");
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [queryDraft, setQueryDraft] = useState(initialLocation.query);
  const [activeQuery, setActiveQuery] = useState(initialLocation.query);
  const [activeSectionId, setActiveSectionId] = useState("");
  const [activeType, setActiveType] = useState<"all" | SearchResultType>("all");
  const [searchResponse, setSearchResponse] = useState<HubSearchResponse | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [selectedSourceIds, setSelectedSourceIds] = useState<string[]>([]);
  const [chatOpen, setChatOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [previewResult, setPreviewResult] = useState<HubSearchResult | null>(null);
  const [previewModule, setPreviewModule] = useState<HubModule | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState("");
  const [previewBrief, setPreviewBrief] = useState<HubSourceBrief | null>(null);
  const [previewBriefLoading, setPreviewBriefLoading] = useState(false);
  const [previewBriefError, setPreviewBriefError] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const deepLinkHandled = useRef(false);

  useEffect(() => {
    const controller = new AbortController();
    setLoadError("");
    Promise.all([getCatalog(controller.signal), getSearchIndex(controller.signal)])
      .then(([nextCatalog, nextIndex]) => {
        setCatalog(nextCatalog);
        setSearchIndex(nextIndex);
      })
      .catch((error) => {
        if (!controller.signal.aborted) {
          setLoadError(error instanceof Error ? error.message : "The MCS source catalog could not be loaded.");
        }
      });
    return () => controller.abort();
  }, [loadAttempt]);

  useEffect(() => {
    if (deepLinkHandled.current || searchIndex.length === 0) return;
    deepLinkHandled.current = true;
    const candidate = searchIndex.find((item) =>
      initialLocation.chapterId
        ? item.chapterId === initialLocation.chapterId
        : initialLocation.moduleId
          ? item.type === "module" && item.moduleId === initialLocation.moduleId
          : false,
    );
    if (candidate) setPreviewResult(withScore(candidate));
  }, [initialLocation.chapterId, initialLocation.moduleId, searchIndex]);

  useEffect(() => {
    const query = activeQuery.trim();
    if (query.length < 2) {
      setSearchResponse(null);
      setSearching(false);
      setSearchError("");
      return;
    }
    const controller = new AbortController();
    setSearching(true);
    setSearchError("");
    searchHub(query, {
      sectionId: activeSectionId || undefined,
      type: activeType === "all" ? undefined : activeType,
      limit: 36,
    }, controller.signal)
      .then(setSearchResponse)
      .catch((error) => {
        if (!controller.signal.aborted) {
          setSearchError(error instanceof Error ? error.message : "Search is temporarily unavailable.");
          setSearchResponse({ query, total: 0, results: [], mode: "manifest-fallback" });
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setSearching(false);
      });
    return () => controller.abort();
  }, [activeQuery, activeSectionId, activeType]);

  useEffect(() => {
    if (!previewResult?.moduleId) {
      setPreviewModule(null);
      setPreviewLoading(false);
      setPreviewError("");
      return;
    }
    const controller = new AbortController();
    setPreviewModule(null);
    setPreviewLoading(true);
    setPreviewError("");
    getModule(previewResult.moduleId, controller.signal)
      .then(setPreviewModule)
      .catch((error) => {
        if (!controller.signal.aborted) {
          setPreviewError(error instanceof Error ? error.message : "This source could not be loaded.");
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setPreviewLoading(false);
      });
    return () => controller.abort();
  }, [previewResult?.moduleId]);

  useEffect(() => {
    if (!previewResult?.id) {
      setPreviewBrief(null);
      setPreviewBriefLoading(false);
      setPreviewBriefError("");
      return;
    }
    const controller = new AbortController();
    setPreviewBrief(null);
    setPreviewBriefLoading(true);
    setPreviewBriefError("");
    getSourceBrief(previewResult.id, controller.signal)
      .then(setPreviewBrief)
      .catch((error) => {
        if (!controller.signal.aborted) {
          setPreviewBriefError(error instanceof Error ? error.message : "This source summary could not be loaded.");
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setPreviewBriefLoading(false);
      });
    return () => controller.abort();
  }, [previewResult?.id]);

  const localById = useMemo(
    () => new Map(searchIndex.map((item) => [item.id, withScore(item)])),
    [searchIndex],
  );
  const currentById = useMemo(
    () => new Map((searchResponse?.results ?? []).map((item) => [item.id, item])),
    [searchResponse],
  );
  const selectedSources = useMemo(
    () => selectedSourceIds
      .map((id) => currentById.get(id) ?? localById.get(id))
      .filter((source): source is HubSearchResult => Boolean(source)),
    [currentById, localById, selectedSourceIds],
  );
  const browseResults = useMemo(
    () => browseLocalSources(searchIndex, activeSectionId, activeType),
    [activeSectionId, activeType, searchIndex],
  );
  const hasBrowseFilter = Boolean(activeSectionId || activeType !== "all");
  const visibleResults = searchResponse?.results ?? (hasBrowseFilter ? browseResults : []);
  const activeSection = catalog?.sections.find((section) => section.id === activeSectionId) ?? null;
  const compactHero = Boolean(activeQuery.trim().length >= 2 || hasBrowseFilter);

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const query = queryDraft.trim();
    setActiveQuery(query);
    setChatOpen(false);
    writeLocation({ query, moduleId: "", chapterId: "" });
  }

  function clearSearch() {
    setQueryDraft("");
    setActiveQuery("");
    setSearchResponse(null);
    setSearchError("");
    writeLocation({ query: "", moduleId: "", chapterId: "" });
    window.setTimeout(() => searchInputRef.current?.focus(), 20);
  }

  function resetWorkspace() {
    setQueryDraft("");
    setActiveQuery("");
    setActiveSectionId("");
    setActiveType("all");
    setSearchResponse(null);
    setSearchError("");
    setChatOpen(false);
    setPreviewResult(null);
    setSidebarOpen(false);
    writeLocation({ query: "", moduleId: "", chapterId: "" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function focusSearch() {
    setChatOpen(false);
    window.setTimeout(() => {
      searchInputRef.current?.focus();
      searchInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 20);
  }

  function chooseSection(sectionId: string) {
    setActiveSectionId(sectionId);
    setSearchResponse(null);
    setSidebarOpen(false);
    if (!activeQuery.trim()) setQueryDraft("");
  }

  function toggleChatSource(result: HubSearchResult) {
    setSelectedSourceIds((current) => {
      if (current.includes(result.id)) return current.filter((id) => id !== result.id);
      return current.length < 6 ? [...current, result.id] : current;
    });
  }

  function showPreview(result: HubSearchResult) {
    setPreviewResult(result);
    writeLocation({
      query: activeQuery,
      moduleId: result.moduleId || "",
      chapterId: result.chapterId || "",
    });
  }

  function closePreview() {
    setPreviewResult(null);
    writeLocation({ query: activeQuery, moduleId: "", chapterId: "" });
  }

  function openResult(result: HubSearchResult) {
    if (result.type !== "resource" || !result.url) {
      showPreview(result);
      return;
    }
    if (isSafePublicUrl(result.url)) {
      window.open(result.url, "_blank", "noopener,noreferrer");
    }
  }

  function startChat() {
    if (selectedSources.length === 0) return;
    setPreviewResult(null);
    setChatOpen(true);
    setSidebarOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  if (!catalog) {
    return (
      <div className="standalone-loading">
        <img className="standalone-loading__lwazi" src={loadError ? "/lwazi/thinking.png" : "/lwazi/searching.png"} alt="" aria-hidden="true" />
        <BrandMark />
        {loadError ? (
          <div className="standalone-loading__error">
            <AlertTriangle size={30} /><h1>Lwazi could not open the MCS library</h1><p>{loadError}</p>
            <button type="button" onClick={() => setLoadAttempt((value) => value + 1)}><RefreshCw size={17} /> Try again</button>
          </div>
        ) : <p><LoaderCircle className="spin" size={26} /> Lwazi is preparing the public MCS sources…</p>}
      </div>
    );
  }

  return (
    <div className={["standalone-app", isEmbedded ? "standalone-app--embedded" : "", chatOpen ? "standalone-app--chat" : ""].filter(Boolean).join(" ")}>
      <a className="skip-link" href="#workspace-main">Skip to knowledge content</a>
      {!isEmbedded ? (
        <AssistantTopBar
          selectedCount={selectedSources.length}
          onHome={resetWorkspace}
          onSearch={focusSearch}
          onChat={startChat}
          onMenu={() => setSidebarOpen(true)}
        />
      ) : null}

      {chatOpen ? (
        <McsChatWorkspace
          sources={selectedSources}
          initialQuery={activeQuery}
          onBack={() => setChatOpen(false)}
          onOpenSource={showPreview}
        />
      ) : (
        <main id="workspace-main" className="assistant-workspace">
          <KnowledgeSidebar
            catalog={catalog}
            activeSectionId={activeSectionId}
            activeType={activeType}
            mobileOpen={sidebarOpen}
            onSectionChange={chooseSection}
            onTypeChange={(type) => { setActiveType(type); setSearchResponse(null); }}
            onReset={() => { setActiveSectionId(""); setActiveType("all"); setSearchResponse(null); }}
            onClose={() => setSidebarOpen(false)}
          />

          <section className="assistant-search-pane">
            <button className="mobile-filter-button" type="button" onClick={() => setSidebarOpen(true)}>
              <PanelLeftOpen size={17} /> Knowledge areas
            </button>
            <section className={["assistant-hero", compactHero ? "assistant-hero--compact" : ""].filter(Boolean).join(" ")}>
              <div className="assistant-hero__pattern" aria-hidden="true" />
              <div className="assistant-hero__content">
                <p className="section-kicker">Lwazi · MCS document search</p>
                <h1>What can I help you <span>find?</span></h1>
                <p>Search the Modified Cash Standard, implementation guidance, reporting resources, examples, and practical tools in one trusted public library.</p>
                <form className="assistant-search-form" onSubmit={handleSearch}>
                  <label><img className="assistant-search-form__lwazi" src="/lwazi/waving.png" alt="" aria-hidden="true" /><Search size={20} aria-hidden="true" /><span className="sr-only">Search MCS guidance</span><input ref={searchInputRef} value={queryDraft} onChange={(event) => setQueryDraft(event.target.value)} maxLength={160} placeholder="Ask about materiality, assets, disclosures, reporting…" /></label>
                  <button className="primary-action" type="submit" disabled={searching || queryDraft.trim().length < 2}>
                    {searching ? <LoaderCircle className="spin" size={18} /> : <FileSearch size={18} />} {searching ? "Searching" : "Find sources"}
                  </button>
                  {(queryDraft || activeQuery) ? <button className="secondary-action" type="button" onClick={clearSearch}><X size={17} /> Clear</button> : null}
                </form>
                <div className="assistant-hero__journey">
                  <img src="/lwazi/journey.png" alt="" aria-hidden="true" />
                  <p><strong>Read. Search. Understand. Verify.</strong><span>Lwazi keeps every answer connected to its source.</span></p>
                </div>
              </div>
              <div className="assistant-hero__mascot">
                <img src="/lwazi/hero.webp" alt="Lwazi, a smiling book character, says: Hi, I'm Lwazi! How can I help?" />
              </div>
            </section>

            {selectedSources.length > 0 ? (
              <section className="selected-source-tray">
                <div className="selected-source-tray__intro"><img src="/lwazi/approved.png" alt="" aria-hidden="true" /><div><p className="section-kicker">Ready for document chat</p><strong>{selectedSources.length} source{selectedSources.length === 1 ? "" : "s"} selected</strong></div></div>
                <div className="selected-source-tray__chips">
                  {selectedSources.map((source, index) => <button type="button" key={source.id} onClick={() => toggleChatSource(source)}><span>{index + 1}</span>{source.title}<X size={13} /></button>)}
                </div>
                <button className="primary-action" type="button" onClick={startChat}><Sparkles size={17} /> Ask Lwazi</button>
              </section>
            ) : null}

            {searching ? (
              <section className="assistant-status assistant-status--loading"><span><img src="/lwazi/searching.png" alt="" aria-hidden="true" /></span><div><p>Lwazi is searching the MCS library</p><strong>Finding and ranking the most relevant public sources…</strong></div></section>
            ) : null}
            {!searching && searchResponse ? (
              <section className="assistant-status">
                <span className={searchResponse.total > 0 ? "assistant-status__found" : ""}><img src={searchResponse.total > 0 ? "/lwazi/found-it.png" : "/lwazi/thinking.png"} alt="" aria-hidden="true" /></span>
                <div><p>Lwazi <b className={`result-mode result-mode--${searchResponse.mode || "manifest-fallback"}`}>{searchResponse.mode === "ai-search" ? "AI Search" : "Catalog search"}</b></p><strong>{searchResponse.total > 0 ? `I found ${searchResponse.total} relevant source${searchResponse.total === 1 ? "" : "s"} for “${searchResponse.query}”.` : `I could not find a strong match for “${searchResponse.query}”.`}</strong></div>
              </section>
            ) : null}
            {searchError ? <p className="workspace-error"><AlertTriangle size={17} /> {searchError}</p> : null}

            {visibleResults.length > 0 ? (
              <section className="assistant-results">
                <header><div><p className="section-kicker">Ranked public sources</p><h2>{activeQuery ? "Search results" : activeSection?.title || "Browse sources"}</h2></div><span>{visibleResults.length} shown</span></header>
                <div className="assistant-results__list">
                  {visibleResults.map((result, index) => (
                    <AssistantSourceCard
                      key={result.id}
                      result={result}
                      rank={index + 1}
                      selectedForChat={selectedSourceIds.includes(result.id)}
                      selectionFull={selectedSourceIds.length >= 6}
                      onPreview={() => showPreview(result)}
                      onOpen={() => openResult(result)}
                      onChatToggle={() => toggleChatSource(result)}
                    />
                  ))}
                </div>
              </section>
            ) : null}

            {!searching && (searchResponse || hasBrowseFilter) && visibleResults.length === 0 ? (
              <section className="empty-results"><img src="/lwazi/thinking.png" alt="" aria-hidden="true" /><h2>Let’s try another search</h2><p>Lwazi could not find a matching MCS source. Try a broader phrase, another knowledge area, or reset the content-type filter.</p><button className="secondary-action" type="button" onClick={() => { setActiveSectionId(""); setActiveType("all"); clearSearch(); }}>Reset search</button></section>
            ) : null}

            {!searchResponse && !hasBrowseFilter ? (
              <section className="welcome-browser">
                <header><div><p className="section-kicker">Explore the hub</p><h2>Browse MCS knowledge areas</h2></div><span>{catalog.sections.length} collections</span></header>
                <div className="welcome-browser__grid">
                  {catalog.sections.map((section, index) => (
                    <button type="button" key={section.id} onClick={() => chooseSection(section.id)}>
                      <span className={`welcome-browser__number welcome-browser__number--${(index % 4) + 1}`}>{String(index + 1).padStart(2, "0")}</span>
                      <div><strong>{section.title}</strong><p>{section.summaryText || `${section.moduleCount} MCS topics and supporting resources.`}</p><small>{section.moduleCount} topics · {section.resourceCount} resources</small></div>
                      <ChevronRight size={19} />
                    </button>
                  ))}
                </div>
                <div className="welcome-browser__note"><Library size={20} /><p><strong>Public, source-led guidance.</strong> Search results link back to the exact MCS chapter or original downloadable resource. Select up to six sources to compare them in chat.</p></div>
              </section>
            ) : null}

            <footer className="assistant-footer"><span>National Treasury · Office of the Accountant-General</span><span>Lwazi · MCS document search · Public information service</span></footer>
          </section>
        </main>
      )}

      {previewResult ? (
        <McsSourcePreviewModal
          result={previewResult}
          catalog={catalog}
          module={previewModule}
          loading={previewLoading}
          error={previewError}
          brief={previewBrief}
          briefLoading={previewBriefLoading}
          briefError={previewBriefError}
          selectedForChat={selectedSourceIds.includes(previewResult.id)}
          selectionFull={selectedSourceIds.length >= 6}
          onClose={closePreview}
          onChatToggle={toggleChatSource}
          onOpenResult={openResult}
          onPreviewResult={showPreview}
        />
      ) : null}
    </div>
  );
}

function browseLocalSources(
  items: SearchIndexItem[],
  sectionId: string,
  type: "all" | SearchResultType,
) {
  const typeOrder: Record<SearchResultType, number> = { module: 0, chapter: 1, resource: 2, section: 3 };
  return items
    .filter((item) => !sectionId || item.sectionId === sectionId)
    .filter((item) => item.type !== "section")
    .filter((item) => type === "all" || item.type === type)
    .sort((left, right) => typeOrder[left.type] - typeOrder[right.type] || left.title.localeCompare(right.title))
    .slice(0, 48)
    .map(withScore);
}

function withScore(item: SearchIndexItem): HubSearchResult {
  const { text: _text, ...result } = item;
  return {
    ...result,
    score: 0,
    chatAvailable: !["LINK", "MP4", "ZIP"].includes((item.format || "").toUpperCase()),
  };
}

function readLocation(): InitialLocation {
  const params = new URL(window.location.href).searchParams;
  return {
    query: params.get("q") || "",
    moduleId: params.get("module") || "",
    chapterId: params.get("chapter") || "",
  };
}

function writeLocation(location: InitialLocation) {
  const url = new URL(window.location.href);
  for (const key of ["q", "module", "chapter"]) url.searchParams.delete(key);
  if (location.query) url.searchParams.set("q", location.query);
  if (location.moduleId) url.searchParams.set("module", location.moduleId);
  if (location.chapterId) url.searchParams.set("chapter", location.chapterId);
  window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
}

function isSafePublicUrl(value: string) {
  try {
    const url = new URL(value, window.location.origin);
    return url.protocol === "https:" || url.protocol === "http:";
  } catch {
    return false;
  }
}
